import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Factory, PackageOpen, HeartPulse, Sun, Sunset, Moon, CalendarDays, ArrowRight, Lock } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HourlyCard } from "@/components/HourlyBits";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSeo, ORG_LD, crumbs } from "@/lib/seo";

const CATS = [["factory", "Factory & CNC", "CNC machinists, welders, assemblers, machine operators, maintenance techs", Factory],
  ["warehouse", "Warehouse & loading", "Pick and pack, forklift, loaders and unloaders, material handlers", PackageOpen],
  ["care", "Caregiving & nursing", "Caregivers, CNAs, home health aides, LPNs and RNs", HeartPulse]];
const SHIFTS = [["1st", "1st shift", "Daytime", Sun], ["2nd", "2nd shift", "Afternoon & evening", Sunset], ["3rd", "3rd shift", "Overnight", Moon], ["weekend", "Weekend", "Fri–Sun rotations", CalendarDays]];
const BANDS = [["17-25", "$17–25/hr"], ["25-40", "$25–40/hr"], ["40-60", "$40–60/hr"], ["60-100", "$60–100/hr"]];
const FAQ = [
  ["Where do these jobs come from?", "From employers' own career sites and from staffing agencies' public job boards. Agency roles are labelled so you always know who is hiring."],
  ["Why do I need a subscription?", "Pay and shift are free to browse. Subscribers see the employer, the full posting and the apply link, plus shift and pay filters across all of Job Discovery."],
  ["Do all listings show pay and shift?", "Yes. A job only appears here if the posting itself states an hourly rate between $17 and $100 and a shift. We never guess pay or shifts."],
  ["Which areas are covered?", "The United States and Canada. Filter by state or province and city once you subscribe."],
];
const n = (v) => (v || 0).toLocaleString();

const Tile = ({ icon: Icon, title, sub, count, testid }) => (
  <Link to="/register?next=/app/hourly" data-testid={testid} className="lift group flex flex-col rounded-2xl border bg-white p-6">
    <Icon className="h-7 w-7 text-primary" />
    <h3 className="mt-4 text-lg font-semibold">{title}</h3>
    <p className="mt-1 flex-1 text-sm text-slate-600">{sub}</p>
    <p className="mt-4 font-mono text-sm font-bold text-emerald-700">{count != null ? `${n(count)} open roles` : "…"} <ArrowRight className="ml-1 inline h-4 w-4 transition-transform group-hover:translate-x-1" /></p>
  </Link>
);

export default function HourlyJobs() {
  const [s, setS] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { api.get("/public/hourly-stats").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  useSeo({ title: "1st, 2nd & 3rd shift jobs paying $17–$100/hr in the US & Canada", path: "/hourly-jobs",
    description: "Hourly shift jobs with posted pay: CNC machinist, welder, pick and pack, forklift, loader, caregiver, CNA, home health aide, LPN and RN roles across the US and Canada.",
    jsonLd: [ORG_LD, crumbs([["Home", "/"], ["Hourly & shift jobs", "/hourly-jobs"]]),
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] });
  const unlock = () => nav(user ? "/app/billing" : "/register?next=/app/hourly");
  return (
    <div className="min-h-screen" data-testid="hourly-public-page">
      <PublicHeader />
      <main className="mx-auto max-w-6xl px-4 sm:px-8">
        <section className="py-14 sm:py-20">
          <p className="eyebrow mb-3">Hourly & shift jobs · US & Canada</p>
          <h1 className="max-w-4xl text-4xl font-extrabold sm:text-5xl lg:text-6xl">Shift jobs paying <span className="text-emerald-700">$17–$100</span> an hour that you never knew existed.</h1>
          <p className="mt-5 max-w-2xl text-base text-slate-600 md:text-lg">
            {s?.total ? `${n(s.total)} open 1st, 2nd and 3rd shift roles` : "Open 1st, 2nd and 3rd shift roles"} in factories, warehouses and care, every one with the pay and shift written in the posting. Many never reach the big job sites.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" className="rounded-full" onClick={unlock} data-testid="hourly-hero-cta">Unlock every shift job <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <Button size="lg" variant="outline" className="rounded-full" asChild data-testid="hourly-pricing-link"><Link to="/pricing">Plans from $14.99/mo</Link></Button>
          </div>
        </section>
        <section className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="hourly-category-tiles">
          {CATS.map(([k, t, sub, I]) => <Tile key={k} icon={I} title={t} sub={sub} count={s?.categories?.[k] ?? (s ? 0 : null)} testid={`hourly-cat-${k}`} />)}
        </section>
        <section className="mt-12 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="hourly-shift-tiles">
          {SHIFTS.map(([k, t, sub, I]) => (
            <div key={k} className="rounded-2xl border bg-white p-5" data-testid={`hourly-shift-${k}`}>
              <I className="h-5 w-5 text-slate-500" /><p className="mt-3 font-semibold">{t}</p><p className="text-xs text-slate-500">{sub}</p>
              <p className="mt-2 font-mono text-2xl font-extrabold">{s ? n(s.shifts?.[k]) : "…"}</p>
            </div>
          ))}
        </section>
        <section className="mt-12 rounded-3xl bg-slate-950 p-6 text-white sm:p-10" data-testid="hourly-pay-bands">
          <h2 className="text-2xl font-bold sm:text-3xl">What these roles pay</h2>
          <p className="mt-2 text-sm text-slate-300">Open roles by posted hourly range. A role can fall into more than one band.</p>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {BANDS.map(([k, t]) => <div key={k} className="rounded-2xl border border-white/10 bg-white/5 p-4"><p className="font-mono text-sm text-emerald-300">{t}</p><p className="mt-1 font-mono text-3xl font-extrabold">{s ? n(s.pay_bands?.[k]) : "…"}</p></div>)}
          </div>
        </section>
        <section className="mt-14" data-testid="hourly-teaser-list">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div><h2 className="text-2xl font-bold sm:text-3xl">Top-paying shift jobs right now</h2><p className="mt-1 text-sm text-slate-600">Employer and apply link unlock with any paid plan.</p></div>
            <Button variant="outline" className="rounded-full" onClick={unlock} data-testid="hourly-teaser-unlock"><Lock className="mr-2 h-4 w-4" />Unlock all</Button>
          </div>
          <div className="mt-6 space-y-3">{(s?.teasers || []).map((j) => <HourlyCard key={j.teaser_id} j={j} onLocked={unlock} />)}</div>
          {s && !s.teasers?.length && <p className="rounded-2xl border bg-white p-8 text-center text-slate-500">New shift jobs are being added. Check back shortly.</p>}
        </section>
        <section className="mt-16 max-w-3xl" data-testid="hourly-faq">
          <h2 className="text-2xl font-bold sm:text-3xl">Questions</h2>
          <Accordion type="single" collapsible className="mt-4">
            {FAQ.map(([q, a], i) => <AccordionItem key={q} value={`f${i}`}><AccordionTrigger data-testid={`hourly-faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>)}
          </Accordion>
        </section>
        <section className="my-16 rounded-3xl border bg-white p-8 text-center sm:p-12">
          <h2 className="text-2xl font-bold sm:text-3xl">Your next shift is already posted.</h2>
          <p className="mx-auto mt-2 max-w-xl text-slate-600">Subscribe to see who is hiring, filter by shift, pay and city, and apply on the employer's own site.</p>
          <Button size="lg" className="mt-6 rounded-full" onClick={unlock} data-testid="hourly-bottom-cta">See plans and unlock</Button>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
